import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { HttpInteresseClientService } from "./http-interesse-client.service";
import type {
  InteresseEmitPolicyApiResponse,
  InteresseEmitPolicyRequestEnvelope,
  InteresseQuoteDataEnvelope,
  InteresseQuoteRequest,
} from "./interesse-quote.types";

/** GET/POST `/insurance/brands` — `data[]` row. */
export interface InteresseApiBrand {
  id: number;
  nombre: string;
}

/**
 * `/insurance/subbrands` and `/insurance/versions` — `data[]` vehicle row.
 * `id_auto` is sent as `vehicle_id` in quote_all; `tipo` groups models; `version` is the trim line.
 */
export interface InteresseVehicleCatalogRow {
  id_auto: number;
  descripcion: string;
  marca_larga: string;
  modelo: string;
  tipo: string;
  version: string;
  uso: string;
  id_tipo_auto: number;
  id_categoria: number;
}

/** @deprecated Use {@link InteresseApiBrand}. */
export type InteresseBrand = InteresseApiBrand;
/** @deprecated Use {@link InteresseVehicleCatalogRow}. */
export type InteresseSubbrand = InteresseVehicleCatalogRow;
/** @deprecated Use {@link InteresseVehicleCatalogRow}. */
export type InteresseVersion = InteresseVehicleCatalogRow;

export interface InteresseQuoteAllResponse {
  status: string;
  data?: InteresseQuoteDataEnvelope;
  errors?: unknown[];
}

/**
 * Interesse insurance API — brands, subbrands (catalog), versions (trims), quote_all.
 * Quote payload uses {@link InteresseQuoteRequest} (not Eikos).
 */
@Injectable({
  providedIn: "root",
})
export class InteresseInsuranceService {
  private readonly basePath = "/api/interesse_api_service/v1/insurance";

  constructor(private http: HttpInteresseClientService) {}

  getBrands(year: number): Observable<InteresseApiBrand[]> {
    return this.http.post(`${this.basePath}/brands`, { year }).pipe(
      map((res: { status?: string; data?: InteresseApiBrand[] }) => res?.data ?? []),
      catchError(() => of([]))
    );
  }

  /** Catalog rows for the brand/year; UI dedupes by `tipo` into models. */
  getSubbrands(year: number, brand_id: number, brand_name: string): Observable<InteresseVehicleCatalogRow[]> {
    return this.http.post(`${this.basePath}/subbrands`, { year, brand_id, brand: brand_name }).pipe(
      map((res: { status?: string; data?: InteresseVehicleCatalogRow[] }) => res?.data ?? []),
      catchError(() => of([]))
    );
  }

  /** Trims for one model line (`tipo`) within brand/year. */
  getVersions(
    year: number,
    brand_id: number,
    brand_name: string,
    tipo: string
  ): Observable<InteresseVehicleCatalogRow[]> {
    return this.http
      .post(`${this.basePath}/versions`, {
        year,
        brand_id,
        brand: brand_name,
        subbrand: tipo,
      })
      .pipe(
        map((res: { status?: string; data?: InteresseVehicleCatalogRow[] }) => res?.data ?? []),
        catchError(() => of([]))
      );
  }

  quoteAll(payload: InteresseQuoteRequest): Observable<InteresseQuoteAllResponse> {
    return this.http.post(`${this.basePath}/quote`, payload).pipe(
      map((res: InteresseQuoteAllResponse) => res ?? { status: "ERROR", errors: [] }),
      catchError(() => of({ status: "ERROR", errors: ["Request failed"] }))
    );
  }

  /**
   * POST `/insurance/emit_policy` — issues policy from a prior Interesse quote.
   * Returns HTTP body as-is (including `status: "ERROR"` with `errors`).
   */
  emitPolicy(payload: InteresseEmitPolicyRequestEnvelope): Observable<InteresseEmitPolicyApiResponse> {
    return this.http.post(`${this.basePath}/emit_policy`, payload).pipe(
      map((res: unknown) => {
        const r = res as InteresseEmitPolicyApiResponse | null | undefined;
        return r ?? { status: "ERROR", data: null, errors: ["Invalid response"] };
      }),
      catchError((err: { error?: InteresseEmitPolicyApiResponse }) => {
        const body = err?.error;
        if (body?.status === "ERROR" || (body?.errors && body.errors.length)) {
          return of({
            status: "ERROR",
            data: body?.data ?? null,
            errors: body?.errors ?? ["Request failed"],
          });
        }
        return of({ status: "ERROR", data: null, errors: ["Request failed"] });
      })
    );
  }
}
